import { Box, Flex } from '@chakra-ui/layout';
import { Button, Card, CardBody, useDisclosure } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidV4 } from 'uuid';

import { AddCommitmentModal, CommitmentFormData } from './AddCommitmentModal';
import { CommitmentTable } from './CommitmentTable';
import { addCommitment } from './dataMappings';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { ShowWhileEditing } from '~/core/routes/settings/implementations/common/DiffInputs';

type OverviewTileProps = { children: React.ReactNode; label: React.ReactNode };
const OverviewTile = ({ children, label }: OverviewTileProps) => (
  <Card variant="outline" flex="1">
    <CardBody display="flex" flexDirection="column" gap="0.5rem">
      <Typography variant="detail-muted">{label}</Typography>
      <Typography variant="base" fontWeight="medium">
        {children}
      </Typography>
    </CardBody>
  </Card>
);

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const CommitmentsSummary = () => {
  const navigate = useNavigate();
  const commitments = useCreditAgreementStore(
    (store) => store.creditAgreement?.loanMechanicsV1?.commitments ?? [],
  );
  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );
  const disclosure = useDisclosure();

  const numberOfCommitments = commitments.length || 0;

  const totalCommitment = commitments
    .filter((c) => !c.limit.sublimit)
    .reduce((p, c) => p + c.limit.amount, 0);

  const totalSublimit = commitments
    .filter((c) => c.limit.sublimit)
    .reduce((p, c) => p + c.limit.amount, 0);

  const totalSublimits =
    commitments.filter((c) => c.limit.sublimit).length || 0;

  const onAddCommitment = useCallback(
    (commitmentForm: CommitmentFormData) => {
      const newKey = uuidV4();
      updateCreditAgreement((d) => addCommitment(d, commitmentForm, newKey));
      navigate(newKey);
    },
    [updateCreditAgreement, navigate],
  );

  return (
    <Flex direction="column">
      <Flex justifyContent="space-between">
        <Box>
          <Typography variant="page-title">Commitments</Typography>
          <Typography variant="base-muted">
            At least one commitment is required per facility. Select
            &apos;Review Changes&apos; to submit for approval.
          </Typography>
        </Box>
        <AddCommitmentModal
          disclosure={disclosure}
          onAddCommitment={onAddCommitment}
        />
        <ShowWhileEditing>
          <Button
            colorScheme="v2-aurora"
            leftIcon={<Icon icon="plus" />}
            onClick={disclosure.onOpen}
            variant="outline"
          >
            Add Commitment
          </Button>
        </ShowWhileEditing>
      </Flex>
      <Flex my="2rem" gap="1rem">
        <OverviewTile label="Total Commitment">
          {currencyFormatter.format(totalCommitment)}
        </OverviewTile>
        <OverviewTile label="Number of Commitments">
          {numberOfCommitments}
        </OverviewTile>
        <OverviewTile label="Total Sublimit">
          {currencyFormatter.format(totalSublimit)}
        </OverviewTile>
        <OverviewTile label="Number of Sublimits">
          {totalSublimits}
        </OverviewTile>
      </Flex>
      {commitments.length > 0 && <CommitmentTable />}
    </Flex>
  );
};
